import { cva } from "class-variance-authority";
import { Check, X } from "lucide-react";

import { Paragraph } from "@/components/ui/typography";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { RadioGroupItem } from "@/components/ui/radio-group";

import { calculatePercentage, cn, formatCurrency } from "@/lib/utils";

export function FeatureItem({ feature, checked }) {
  return (
    <li
      className="flex items-start gap-x-2 group"
      data-feature-type={checked ? "checked" : "unchecked"}
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center size-4.5 rounded-sm mt-0.5",
          "group-data-[feature-type=checked]:bg-success-bg group-data-[feature-type=unchecked]:bg-danger-bg"
        )}
      >
        <Check
          size={12}
          strokeWidth={4}
          className="group-data-[feature-type=checked]:block hidden text-success"
        />
        <X
          size={12}
          strokeWidth={4}
          className="group-data-[feature-type=unchecked]:block hidden text-danger"
        />
      </div>

      <Paragraph variant="b4" className="text-grayscale-label">
        {feature}
      </Paragraph>
    </li>
  );
}

const packageVariants = cva(
  "flex items-center py-1.5 px-3 bg-primary text-primary-foreground",
  {
    variants: {
      variant: {
        platinum:
          "gradient-course-package-platinum !text-course-package-platinum",
        gold: "gradient-course-package-gold !text-course-package-gold",
        silver: "gradient-course-package-silver !text-course-package-silver",
        default: "grayscale-input grayscale-header-weak",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export function CoursePackageMobile({
  coursePackage,
  variant,
  isAutopay = false,
  ...props
}) {
  if (!coursePackage) return null;

  let packageValidityString = coursePackage.packageValidity;
  if (!isAutopay && coursePackage.offerValidity > 0) {
    packageValidityString += ` + ${coursePackage.offerValidity}`;
  }

  return (
    <Label
      className={cn(
        "flex-col items-stretch rounded-lg overflow-hidden gap-0 border border-transparent",
        "has-data-[state=checked]:border-primary not-disabled:cursor-pointer"
      )}
      {...props}
    >
      <div className={cn("justify-center", packageVariants({ variant }))}>
        <Paragraph variant="b3-tight" weight="sb" className="text-inherit">
          {coursePackage.packageName}
        </Paragraph>
      </div>

      <div className="flex flex-col gap-y-1 p-3 border border-grayscale-input border-t-0 rounded-b-lg">
        <Paragraph variant="b4-tight" className="text-grayscale-placeholder">
          Validity
        </Paragraph>

        <Paragraph variant="b3" weight="sb" className="text-grayscale-header">
          {packageValidityString} Months
        </Paragraph>
      </div>

      <RadioGroupItem value={coursePackage.packageId} className="hidden" />
    </Label>
  );
}

function CoursePackage({
  coursePackage,
  variant,
  collapsed,
  isAutopay = false,
  ...props
}) {
  if (!coursePackage) return null;

  const packageSellingPrice = isAutopay
    ? coursePackage.autoPayPackageSellingPrice
    : coursePackage.packageSellingPrice;
  const packageBasePrice = isAutopay
    ? coursePackage.autoPayPackageBasePrice
    : coursePackage.packageBasePrice;
  const packageValidity = isAutopay
    ? coursePackage.autoPayPackageValidity
    : coursePackage.packageValidity;

  const percentageOff = calculatePercentage(
    packageSellingPrice,
    packageBasePrice,
    true
  );

  let packageValidityString = packageValidity;
  if (!isAutopay && coursePackage.offerValidity > 0) {
    packageValidityString += ` + ${coursePackage.offerValidity}`;
  }

  return (
    <Label
      className={cn(
        "flex-col items-stretch border border-grayscale-input rounded-lg overflow-hidden transition-colors duration-300",
        "has-data-[state=checked]:border-primary not-disabled:cursor-pointer",
        "has-disabled:cursor-not-allowed has-disabled:opacity-50 has-disabled:border-grayscale-line!"
      )}
      {...props}
    >
      <div className={packageVariants({ variant })}>
        <Paragraph variant="b4" weight="sb" className="text-inherit">
          {coursePackage.packageName}
        </Paragraph>
      </div>

      <div className="flex flex-col px-3 py-4">
        <Paragraph
          variant="b5"
          weight="sb"
          className="text-grayscale-placeholder"
        >
          Validity
        </Paragraph>
        <Paragraph
          variant="b3"
          weight="sb"
          className="text-grayscale-header-weak"
        >
          {packageValidityString} Months
        </Paragraph>

        <Separator className="my-2 bg-grayscale-input" />

        <div className="flex items-end justify-between">
          <div className="flex flex-col min-h-9">
            <Paragraph
              variant="b4"
              weight="sb"
              className="text-grayscale-header"
            >
              <span>{formatCurrency(packageSellingPrice)}</span>
            </Paragraph>

            {percentageOff > 0 && (
              <div className="flex items-center gap-x-2">
                <Paragraph
                  variant="b5"
                  className="text-grayscale-placeholder line-through"
                >
                  <span>{formatCurrency(packageBasePrice)}</span>
                </Paragraph>

                <Paragraph variant="b5" className="text-success">
                  ({percentageOff}% off)
                </Paragraph>
              </div>
            )}
          </div>

          <RadioGroupItem
            value={coursePackage.packageId}
            className="size-6 border-2 [&_.lucide-circle]:size-3 data-[state=unchecked]:border-grayscale-line"
          />
        </div>

        <ul
          className={cn(
            "flex flex-col gap-y-2 max-h-0 overflow-auto opacity-0 transition-all duration-300 scrollbar-hide",
            "hover:scrollbar-default lg:group-hover:max-h-120 lg:group-hover:mt-4.5 lg:group-hover:opacity-100 group-hover:delay-200",
            !collapsed && "max-h-120 mt-4.5 opacity-100"
          )}
        >
          {coursePackage.features.map((feature) => (
            <FeatureItem
              key={feature.featureId}
              feature={feature.featureName}
              checked={!!feature.status}
            />
          ))}
        </ul>
      </div>
    </Label>
  );
}

export default CoursePackage;
